import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ClientEnvironment, CommonUtilityLib, ConfigObjectModel, ENVIRONMENT, SystemPreferencesService } from '@thehubz/client/core';
import { CollectionsApiService } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType } from '@thehubz/shared/model';
import { Subscription, debounce, tap, timer } from 'rxjs';
import { CollectionInfo } from '../../collection-info.model';
import { CollectionAssetPreviewService } from '../../services/collection-asset-preview.service';
import { CollectionEventsService } from '../../services/collection-events.service';
import { SelectCollectionDialogService } from '../../services/select-collection-dialog.service';

@Component({
  selector: 'thehubz-select-collection-popup',
  templateUrl: './select-collection-popup.component.html',
  styleUrls: ['./select-collection-popup.component.scss'],
})
export class SelectCollectionPopupComponent implements OnInit, OnDestroy {
  collectionsList: CollectionInfo[] = [];
  selectedCollectionList: CollectionInfo[] = [];
  finalSelectedCollectionList: string[] = [];
  basketDataLoaded = false;
  configObject: ConfigObjectModel = {
    title: $localize`Published collection`,
    callFrom: 'dashboard',
  };
  searchText = '';
  brandingKey!: CustomClients;
  CustomClients = CustomClients;
  isMobileSearchOpen = false;
  searchPlaceholder = $localize`:placeholder:Search...`;
  filterObject = {
    filters: {
      assetId: '',
      search: '',
    },
    sort: { createdAt: -1 },
  };
  collectionListSearchFilter!: Subscription;
  /**
   * True when data is loading from server
   */
  httpCallInProgress = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) protected readonly data: AnyType,
    @Inject(ENVIRONMENT) env: ClientEnvironment,
    private dialog: MatDialog,
    private commonUtilityLib: CommonUtilityLib,
    private collectionAPI: CollectionsApiService,
    private systemPreferenceService: SystemPreferencesService,
    public dialogRef: MatDialogRef<SelectCollectionPopupComponent>,
    private stepperDialogService: SelectCollectionDialogService,
    private collectionAssetPreviewService: CollectionAssetPreviewService,
    private collectionEventsService: CollectionEventsService,
  ) {
    this.brandingKey = env.brandingKey ?? CustomClients.Standard;
  }

  ngOnInit(): void {
    // resigning empty string because search value assigned at parent component (collection.component.ts)
    if (this.data && this.data.selectedCollection) {
      this.selectedCollectionList = JSON.parse(JSON.stringify(this.data.selectedCollection));
      this.selectedCollectionList.forEach((collection: AnyType) => {
        collection.checked = true;
      });
    }
    this.setSelectedCollectionList();
    this.collectionEventsService.collectionListSearchFilter$.next('');
    this._showLoader();
    // subscribe to the search filter
    this.collectionListSearchFilter = this.collectionEventsService.collectionListSearchFilter$.pipe(debounce(() => timer(500))).subscribe((data) => {
      if (data) {
        this.fetchCollections(data);
      } else {
        this.fetchCollections('');
      }
    });
    this.commonUtilityLib.emitNewHeaderline($localize`Published collection`);
  }

  /**
   * get all collection list of logged in user
   * @param searchText
   */
  async fetchCollections(searchText?: string) {
    this.filterObject['filters']['search'] = searchText || '';
    if (this.data.callFrom && this.data.callFrom == 'collection-details-view' && this.data['collectionList']) {
      this.collectionsList = JSON.parse(JSON.stringify(this.data.collectionList));
      this.selectedCollectionList.forEach((collection: AnyType) => {
        const index = this.collectionsList.findIndex((publishedCollections: AnyType) => publishedCollections._id == collection._id);
        if (index != -1) {
          this.collectionsList[index].checked = true;
        }
      });
    } else {
      this.collectionAPI
        .collectionsControllerFindAllPublishedCollection(JSON.stringify(this.filterObject))
        .pipe(tap(() => this._showLoader(false)))
        .subscribe({
          next: async (response: AnyType) => {
            this.collectionsList = response.result;
            if (this.collectionsList.length) {
              this.selectedCollectionList.forEach((collection: AnyType) => {
                const index = this.collectionsList.findIndex((publishedCollections: AnyType) => publishedCollections._id == collection._id);
                if (index != -1) {
                  this.collectionsList[index].checked = true;
                }
              });
              this.collectionsList = await this.collectionAssetPreviewService.generateCoverPreview(this.collectionsList);
              this.basketDataLoaded = true;
            } else {
              this.basketDataLoaded = true;
            }
          },
        });
    }
  }

  /**
   * cdkDropListDropped event method
   * @param event
   */
  drop(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
  }

  updateSelection(event: AnyType) {
    if (event.published) {
      if (event.evt.checked) {
        this.collectionsList[event.index].checked = true;
        this.selectedCollectionList.push(event.item);
      } else {
        this.collectionsList[event.index].checked = false;
        const selectedIndex = this.selectedCollectionList.findIndex((collection: AnyType) => event.item._id == collection._id);
        if (selectedIndex != -1) {
          this.selectedCollectionList.splice(selectedIndex, 1);
        }
      }
    } else {
      const publishedIndex = this.collectionsList.findIndex((collection: AnyType) => event.item._id == collection._id);
      if (publishedIndex != -1) {
        this.collectionsList[publishedIndex].checked = false;
      }
      this.selectedCollectionList.splice(event.index, 1);
    }
  }
  /**
   * SearchField methods
   */

  searchTextChanged(event: AnyType) {
    this.searchText = event;
    this.collectionEventsService.collectionListSearchFilter$.next(event);
  }

  clearSearch(event: AnyType) {
    this.searchText = event;
    this.collectionEventsService.collectionListSearchFilter$.next(event);
  }

  toggleMobileSearch() {
    this.isMobileSearchOpen = !this.isMobileSearchOpen;
    if (this.isMobileSearchOpen) {
      document.querySelector('.actions-container')?.classList.remove('mb-8');
    }
    if (!this.isMobileSearchOpen) {
      this.searchText = '';
      document.querySelector('.actions-container')?.classList.add('mb-8');
    }
  }

  /**
   * Utility method to show/hide loader
   */
  private _showLoader(show = true) {
    this.httpCallInProgress = show;
  }

  async applyChanges() {
    if (this.setSelectedCollectionList()) {
      await this.systemPreferenceService.updateUserPreferences(
        { brandingKey: this.brandingKey, dashboardCollection: this.finalSelectedCollectionList },
        'collection',
      );
      this.dialogRef.close(true);
    } else {
      this.dialogRef.close(true);
    }
  }

  setSelectedCollectionList() {
    const newSelectedList: string[] = [];
    let changedFlag = true;
    this.selectedCollectionList.forEach((collection: AnyType) => {
      newSelectedList.push(collection._id);
    });
    if (JSON.stringify(newSelectedList) == JSON.stringify(this.finalSelectedCollectionList)) {
      changedFlag = false;
    } else {
      this.finalSelectedCollectionList = newSelectedList;
    }
    return changedFlag;
  }

  closeModal() {
    if (this.setSelectedCollectionList()) {
      this.stepperDialogService.confirmDialogClose(this.dialog, this.dialogRef);
    } else {
      this.dialogRef.close();
    }
  }

  ngOnDestroy(): void {
    this.collectionListSearchFilter.unsubscribe();
  }
}
